import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import FAQCategory from '../components/FAQ/FAQCategory';
import FAQSearch from '../components/FAQ/FAQSearch';
import ContactSection from '../components/FAQ/ContactSection';
import type { FAQContent, FAQCategory as FAQCategoryType } from '@/types/faq';
import './FAQPage.css';

const FAQPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [content, setContent] = useState<FAQContent | null>(null);
  const [filteredCategories, setFilteredCategories] = useState<FAQCategoryType[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasRenderedCategories, setHasRenderedCategories] = useState(false);
  const navigate = useNavigate();
  const isInitialMount = useRef(true);

  useEffect(() => {
    fetchFAQContent();
  }, []);

  useEffect(() => {
    if (content) {
      filterCategories();
    }
  }, [searchQuery, content]);

  // Handle hash focusing after categories are rendered
  useEffect(() => {
    if (!loading && hasRenderedCategories) {
      const hash = window.location.hash;
      console.log('FAQPage hash after render:', hash);

      if (hash) {
        const idToFocus = hash.substring(1);
        
        // Small delay to ensure DOM is fully updated
        setTimeout(() => {
          const element = document.getElementById(idToFocus);
          console.log('FAQPage element after timeout:', element);
          
          if (element) {
            // For non-interactive elements, add tabIndex if needed
            if (!element.hasAttribute('tabindex')) {
              element.setAttribute('tabindex', '-1');
            }
            
            element.focus();
            
            // Also scroll into view for better UX
            element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        }, 100); // Small delay to ensure rendering is complete
      }
    }
  }, [loading, hasRenderedCategories]);

  // Monitor when filtered categories have changed (rendered)
  useEffect(() => {
    if (!loading && content) {
      // Set a flag that categories have been rendered
      setHasRenderedCategories(true);
    }
  }, [filteredCategories, loading, content]);

  const fetchFAQContent = async () => {
    try {
      const response = await fetch('/pages/faq/content.json');
      const data: FAQContent = await response.json();
      setContent(data);
      setFilteredCategories(data.faqCategories);
    } catch (error) {
      console.error('Error loading FAQ content:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterCategories = () => {
    if (!content) return;

    if (!searchQuery.trim()) {
      setFilteredCategories(content.faqCategories);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = content.faqCategories.map(category => {
      const filteredQuestions = category.questions.filter(question =>
        question.question.toLowerCase().includes(query) ||
        question.answer.toLowerCase().includes(query)
      );

      return {
        ...category,
        questions: filteredQuestions
      };
    }).filter(category => category.questions.length > 0);

    setFilteredCategories(filtered);
    
    // Reset render flag when categories change
    setHasRenderedCategories(false);
  };

  const handleContactClick = (link: string|null = null) => {
    console.log('Contact admin clicked', link);
    
    if (!link) return;
    
    if (link.startsWith('/')) { 
      navigate(link);
    } else if (link.startsWith('mailto:') || link.startsWith('tel:') || link.startsWith('http')) {
      console.log('External link:', link);
      const newWindow = window.open(link, '_blank', 'noopener,noreferrer');
      if (newWindow) {
        newWindow.focus();
      }
    } else {
      console.log('Custom action:', link);
      window.location.href = link;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-transparent flex items-center justify-center">
        <div className="text-white">Loading FAQ...</div>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="min-h-screen bg-transparent flex items-center justify-center">
        <div className="text-white">Failed to load FAQ content</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-transparent text-white">
      {/* Header */}
      <div className="container">
        <button 
          className="flex items-center gap-3 bg-transparent text-white/80 hover:text-white transition-colors mt-8 p-1"
          onClick={() => window.history.back()}
        >
          <div className="w-6 h-6 flex items-center justify-center">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </div>
          <span className="text-lg font-medium">Back</span>
        </button>

        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="text-6xl mb-4">🧑‍🚀</div>
          <h1 className="text-5xl font-normal mb-4 tracking-wide">
            How can we help you?
          </h1>
          <p className="text-white/70 text-xl mb-8">
            Find answers to frequently asked questions about SKILLSTURE
          </p>
          
          <FAQSearch
            value={searchQuery}
            onChange={setSearchQuery}
          />
        </div>

        {/* FAQ Categories */}
        <div className="w-full space-y-10 mb-16">
          {filteredCategories.length > 0 ? (
            filteredCategories.map((category) => (
              <FAQCategory key={category.id} category={category} />
            ))
          ) : (
            <div className="text-center py-12">
              <p className="text-white/70 text-xl">
                No results found for "{searchQuery}"
              </p>
            </div>
          )}
        </div>

        {/* Contact Section */}
        {content.contactSection?.buttonLink && <div className="w-full mb-16">
          <ContactSection
            title={content.contactSection.title}
            description={content.contactSection.description}
            buttonText={content.contactSection.buttonText}
            onContactClick={() => handleContactClick(content.contactSection?.buttonLink || null)}
          />
        </div>}
      </div>
    </div>
  );
};

export default FAQPage;